import { Shopify } from "./shopify";
import { Variant, Amount } from "./product";

export function setupCartButton() {
    const checkoutButton = document.getElementById("go-to-cart");

    if(!checkoutButton) {
        throw new Error("Could not find checkout button");
    }

    checkoutButton.addEventListener("click", async function() {
        window.location.href = "./cart";
    });
}

export class Attribute {
    key = "";
    value = "";
    constructor(key: string, value: string) {
        this.key = key;
        this.value = value;
    }
}

export class BaseLineItem {
    merchandiseId: string;
    quantity: number;
    attributes: Array<Attribute> = [];

    constructor(merchandiseId: string, quantity: number) {
        this.quantity = quantity;
        this.merchandiseId = merchandiseId;
    }
}

export class CartLineItem extends BaseLineItem {
    id = ""; // A unique id for this line item in this cart
    totalAmount: Amount;
    featuredImage: string | null = null;
    description: string = "";
    variant: Variant;
    productTitle: string;

    constructor(lineItemId: string, productTitle: string, quantity: number, variant: Variant, totalAmount: Amount) {
        super(variant.id, quantity);
        this.id = lineItemId;
        this.productTitle = productTitle;
        this.variant = variant;
        this.totalAmount = totalAmount;
    }
}

export class CartContents {
    id: string;
    totalAmount: Amount;
    subtotalAmount: Amount; // Value before discounts/taxes
    lineItems: Array<CartLineItem> = [];
    discounts: Array<Amount> = [];

    constructor(id: string, totalAmount: Amount, subtotalAmount: Amount) {
        this.id = id;
        this.totalAmount = totalAmount;
        this.subtotalAmount = subtotalAmount;
    }

    addLineItem(lineItem: CartLineItem) {
        this.lineItems.push(lineItem);
    }
}


const CART_ID_KEY = "cartadisera-cartId";

export class CartUpdatedEvent extends Event {
    static eventName = "cartUpdated";
    cartContents: CartContents;

    constructor(cartContents: CartContents) {
        super(CartUpdatedEvent.eventName);
        this.cartContents = cartContents;
    }
}

export class CartEventTarget extends EventTarget {
    dispatchCartUpdated(cartContents: CartContents) {
        this.dispatchEvent(new CartUpdatedEvent(cartContents));
    }
    addCartUpdatedListener(listener: (evt: CartUpdatedEvent) => void) {
        this.addEventListener(CartUpdatedEvent.eventName, (event) => {
            listener(event as CartUpdatedEvent);
        });
    }
}

export class Cart {

    cartEvents: CartEventTarget;

    private static instance: Cart;
    static getInstance() {
        if(Cart.instance === undefined) {
            Cart.instance = new Cart();
        }
        return Cart.instance;
    }

    constructor() {
        this.cartEvents = new CartEventTarget();
    }

    // Get the cart id from local storage or create a new cart using the Shopify API
    async getOrCreateCart() : Promise<string> {
        let cartId = localStorage.getItem(CART_ID_KEY);
        if(cartId !== null) {

            // Validate the cart id
            try {
                await this.getCart(cartId);
                return cartId;
            } catch (e) {
                // Some error, we better create a new cart
                console.error(e);
                console.error("Cart id is invalid, creating a new cart");
                localStorage.removeItem(CART_ID_KEY);
            }

        }
        // Create a new cart
        const mutation = `
            mutation cartCreate {
                cartCreate {
                    cart {
                        id
                    }
                    userErrors {
                        field
                        message
                    }
                }
            }`;
        // Use Shopify's GraphQL API to create a cart
        const {data, errors, extensions} = await Shopify.getInstance().request(mutation);

        if(!!errors) {
            console.error(errors);
            throw new Error("Could not create cart");
        }

        // Throw on any user errors
        if(!!data.cartCreate.userErrors && data.cartCreate.userErrors.length > 0) {
            console.error(JSON.stringify(data.cartCreate.userErrors));
            throw new Error("Could not create cart");
        }

        const retrievedCartId: string = data.cartCreate.cart.id;
        localStorage.setItem(CART_ID_KEY, retrievedCartId);

        return retrievedCartId;
    }

    async addVariantToCart(variant: Variant, attributes: Array<Attribute> = []) : Promise<CartContents> {
        const cartId = await this.getOrCreateCart();
        const lineItem: BaseLineItem = new BaseLineItem(variant.id, 1);
        lineItem.attributes = attributes;
        let cartContents = await this.addLineItemsToCart(cartId, lineItem);
        this.cartEvents.dispatchCartUpdated(cartContents);
        return cartContents;
    }

    private static cartQuery = `
        id
        cost {
            subtotalAmount {
                amount
                currencyCode
            }
            totalAmount {
                amount
                currencyCode
            }
        }
        discountAllocations {
            discountedAmount {
                amount
                currencyCode
            }
        }
        lines(first: 10) {
            edges {
                node {
                    id
                    attributes {
                        key
                        value
                    }
                    cost {
                        totalAmount {
                            amount
                            currencyCode
                        }
                    }
                    quantity
                    merchandise {
                        ... on ProductVariant {
                            id
                            title
                            product {
                                featuredImage {
                                    url(transform: { maxWidth: 400, maxHeight: 400 })
                                }
                                title
                            }
                            price {
                                amount
                                currencyCode
                            }
                            selectedOptions {
                                name
                                value
                            }
                        }
                    }
                }
            }
        }
    `;

    cartDataToCartContents(cartData: any) : CartContents {

        // Check if cartData has a field of `cost` and `cost.totalAmount`
        if(!cartData.cost) {
            throw new Error(`Cart data does not have a cost or totalAmount", ${cartData}`);
        }
        if(!cartData.cost.totalAmount) {
            throw new Error(`Cart data does not have a totalAmount", ${cartData}`);
        }
        if(!cartData.cost.subtotalAmount) {
            throw new Error(`Cart data does not have a subtotalAmount", ${cartData}`);
        }

        // Ensure totalAmount is a valid object
        if(!cartData.cost.totalAmount.amount || !cartData.cost.totalAmount.currencyCode) {
            throw new Error(`Cart data does not have a valid totalAmount", ${cartData}`);
        }

        // Ensure subtotalAmount is a valid object
        if(!cartData.cost.subtotalAmount.amount || !cartData.cost.subtotalAmount.currencyCode) {
            throw new Error(`Cart data does not have a valid subtotalAmount", ${cartData}`);
        }

        // Create an Amount object for the total amount
        const totalValueAmount = Number.parseFloat(cartData.cost.totalAmount.amount);
        const totalAmount = new Amount(totalValueAmount, cartData.cost.totalAmount.currencyCode);

        // Create an Amount object for the subtotal amount
        const subtotalValueAmount = Number.parseFloat(cartData.cost.subtotalAmount.amount);
        const subtotalAmount = new Amount(subtotalValueAmount, cartData.cost.subtotalAmount.currencyCode);

        const cartContents = new CartContents(cartData.id, totalAmount, subtotalAmount);

        for(let item of cartData.lines.edges) {
            let line = item.node;

            // Check line has a field of `id`
            if(!line.id) {
                throw new Error(`Line item does not have an id", ${line}`);
            }

            // Check line has a field of `quantity`
            if(!line.quantity) {
                throw new Error(`Line item does not have a quantity", ${line}`);
            }

            // Check if quantity is non-zero number
            if(typeof line.quantity !== "number" || line.quantity <= 0) {
                throw new Error(`Line item has invalid quantity", ${line}`);
            }

            // Check if line has a field of `merchandise`
            if(!line.merchandise || !line.merchandise.product) {
                throw new Error(`Line item does not have a merchandise or product", ${line}`);
            }

            // Check if merchandise has a field of `selectedOptions`
            if(!line.merchandise.selectedOptions) {
                throw new Error(`Line item does not have selectedOptions", ${line}`);
            }

            // Populate selected options
            let selectedOptions: Array<{name: string, value: string}> = [];
            for(let option of line.merchandise.selectedOptions) {
                // Check if option has a field of `name` and `value`
                if(option.name && option.value) {
                    selectedOptions.push({
                        name: option.name,
                        value: option.value
                    });
                }
            }

            // Check if line has a field of `unitPrice`
            if(!line.merchandise.price || !line.merchandise.price.amount || !line.merchandise.price.currencyCode) {
                throw new Error(`Line item does not have a unitPrice", ${line}`);
            }

            // Create a new amount for the unit price
            let valueAmount = Number.parseFloat(line.merchandise.price.amount);
            let unitPrice = new Amount(valueAmount, line.merchandise.price.currencyCode);

            // Make variant from the merchandise
            let variant = new Variant(line.merchandise.id, unitPrice, selectedOptions);

            // Check if merchandise has field of `title`
            if(!line.merchandise.product.title || line.merchandise.product.title.length === 0) {
                throw new Error(`Line item does not have a product title", ${line}`);
            }
            let productTitle = line.merchandise.product.title;

            // Check if line has a field of `cost`
            if(!line.cost || !line.cost.totalAmount) {
                throw new Error(`Line item does not have a cost or totalAmount", ${line}`);
            }
            // Make sure totalAmount is a valid object
            if(!line.cost.totalAmount.amount || !line.cost.totalAmount.currencyCode) {
                throw new Error(`Line item does not have a valid totalAmount", ${line}`);
            }
            // Create a new TotalAmount object for the line item
            let valueLineAmount = Number.parseFloat(line.cost.totalAmount.amount);
            let lineTotalAmount = new Amount(valueLineAmount, line.cost.totalAmount.currencyCode);

            // Check if line has a field of `attributes`
            if(!line.attributes || !Array.isArray(line.attributes)) {
                throw new Error(`Line item does not have attributes", ${line}`);
            }

            let lineItem = new CartLineItem(line.id, productTitle, line.quantity, variant, lineTotalAmount);

            // Check if merchandise has field of `featuredImage`
            if(!line.merchandise.product.featuredImage || !line.merchandise.product.featuredImage.url) {
                throw new Error(`Line item does not have a featured image", ${line}`);
            }
            lineItem.featuredImage = line.merchandise.product.featuredImage ? line.merchandise.product.featuredImage.url : null;

            // Check if attributes has key and value
            for(let attribute of line.attributes) {
                if(!attribute.key || !attribute.value) {
                    throw new Error(`Line item attribute does not have key or value", ${attribute}`);
                }
                // Create a new Attribute object
                let attr = new Attribute(attribute.key, attribute.value);
                // Add the attribute to the line item
                lineItem.attributes.push(attr);
            }

            cartContents.addLineItem(lineItem);
        }

        // Process cart-level discounts
        if (cartData.discountAllocations && Array.isArray(cartData.discountAllocations)) {
            for (let discount of cartData.discountAllocations) {
                if (!discount.discountedAmount || !discount.discountedAmount.amount || !discount.discountedAmount.currencyCode) {
                    throw new Error(`Discount allocation does not have a valid 'discountedAmount'", ${discount}`);
                }
                // Create a new Amount object for the discount
                const discountValue = Number.parseFloat(discount.discountedAmount.amount);
                if (!isNaN(discountValue) && discountValue > 0) {
                    cartContents.discounts.push(new Amount(discountValue, discount.discountedAmount.currencyCode));
                }
            }
        }

        return cartContents;
    }

    async addLineItemsToCart(cartId: string, ...lineItem: BaseLineItem[]) : Promise<CartContents> {

        // Use Shopify's GraphQL API to add to cart
        const mutation = `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
                cart {
                    ${Cart.cartQuery}
                }
                userErrors {
                    field
                    message
                }
            }
        }`;

        const {data, errors, extensions} = await Shopify.getInstance().request(mutation, {
            variables: {
                cartId: cartId,
                lines: [...lineItem],
            },
        });

        if(!!errors) {
            console.error(errors);
            throw new Error("Could not add line item to cart");
        }

        // Throw on any user errors
        if(!!data.cartLinesAdd.userErrors && data.cartLinesAdd.userErrors.length > 0) {
            console.error(JSON.stringify(data.cartLinesAdd.userErrors));
            throw new Error("Could not add line item to cart");
        }

        const cartContents = this.cartDataToCartContents(data.cartLinesAdd.cart);
        this.cartEvents.dispatchCartUpdated(cartContents);
        return cartContents;
    }

    // Get the cart from the Shopify API
    async getCart(cartId: string) : Promise<CartContents> {

        // Use Shopify's GraphQL API to get the cart
        const query = `query getCart($cartId: ID!){
            cart(id: $cartId) {
                ${Cart.cartQuery}
            }
        }
        `;

        const {data, errors, extensions} = await Shopify.getInstance().request(query, {
            variables: {
                cartId: cartId,
            },
        });

        if(!!errors) {
            console.error(errors);
            throw new Error("Could not get cart contents");
        }

        if(!!data.cart === false) {
            throw new Error("Cart not found");
        }

        const cartContents = this.cartDataToCartContents(data.cart);
        this.cartEvents.dispatchCartUpdated(cartContents);
        return cartContents;
    }

    async updateQuantityInCart(cartId: string, lineItemId: string, newQuantity: Number) : Promise<CartContents> {
        // Use Shopify's GraphQL API to add to cart
        const mutation = `mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
                cart {
                    ${Cart.cartQuery}
                }
                userErrors {
                    field
                    message
                }
            }
        }`;

        const updatedLineItem = { id: lineItemId, quantity: newQuantity };

        const { data, errors } = await Shopify.getInstance().request(mutation, {
            variables: {
                cartId: cartId,
                lines: [updatedLineItem],
            },
        });

        if(!!errors) {
            console.error(errors);
            throw new Error("Could not add line item to cart");
        }

        // Throw on any user errors
        if(!!data.cartLinesUpdate.userErrors && data.cartLinesUpdate.userErrors.length > 0) {
            console.error(JSON.stringify(data.cartLinesUpdate.userErrors));
            throw new Error("Could not update quantity line item");
        }

        const cartContents = this.cartDataToCartContents(data.cartLinesUpdate.cart);
        this.cartEvents.dispatchCartUpdated(cartContents);
        return cartContents;
    }

    // Apply a discount code to the cart using `cartDiscountCodesUpdate`
    async applyDiscountCode(cartId: string, discountCodes: string[]) : Promise<CartContents> {
        // Use Shopify's GraphQL API to apply a discount code
        const mutation = `mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
            cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                cart {
                    ${Cart.cartQuery}
                }
                userErrors {
                    field
                    message
                }
            }
        }`;

        const {data, errors, extensions} = await Shopify.getInstance().request(mutation, {
            variables: {
                cartId: cartId,
                discountCodes: discountCodes,
            },
        });

        if(!!errors) {
            console.error(errors);
            throw new Error("Could not apply discount code to cart.");
        }

        if(!!data.cartDiscountCodesUpdate === false) {
            throw new Error("Error applying discount code to cart, field 'cartDiscountCodesUpdate' was was not found in the returned cart data.");
        }

        if(!!data.cartDiscountCodesUpdate.cart === false) {
            throw new Error("Error applying discount code to cart, field 'cartDiscountCodesUpdate.cart' was was not found in the returned cart data.");
        }

        const cartContents = this.cartDataToCartContents(data.cartDiscountCodesUpdate.cart);
        this.cartEvents.dispatchCartUpdated(cartContents);
        return cartContents;
    }
}